/** Типы страницы «Продажи». Контракт — docs/SPEC_2026-10-20_ANALYTICS_WAVE2_SALES.md, раздел 3.3. */

import type { DrillDimension } from '../shared/drill'

/** Метрики продаж. Любая может быть `null`: «нет данных» не равно «ноль». */
export interface SalesMetrics {
  ordersQty: number | null
  ordersRub: number | null
  ordersBuyerRub: number | null
  sppRub: number | null
  cancelsQty: number | null
  buyoutsQty: number | null
  buyoutsRub: number | null
  /** Скидка площадки в выкупах, ₽: WB — СПП, OZON — скидка за счёт OZON; только выкупы с известной ценой покупателя. */
  mpDiscountRub: number | null
  /** Выкупы по цене продавца с известной ценой покупателя, ₽ — база доли скидки площадки. */
  mpDiscountBaseRub: number | null
  returnsQty: number | null
  returnsRub: number | null
  /** Доля 0..1: выкупы / заказы (уточнение архитектора к контракту 3.3). */
  buyoutPercent: number | null
  avgCheck: number | null
  articlesWithOrders: number | null
  designsWithOrders: number | null
}

export type SalesMetricKey = keyof SalesMetrics

export const SALES_METRIC_KEYS: SalesMetricKey[] = [
  'ordersQty',
  'ordersRub',
  'ordersBuyerRub',
  'sppRub',
  'cancelsQty',
  'buyoutsQty',
  'buyoutsRub',
  'mpDiscountRub',
  'mpDiscountBaseRub',
  'returnsQty',
  'returnsRub',
  'buyoutPercent',
  'avgCheck',
  'articlesWithOrders',
  'designsWithOrders',
]

export interface SalesFreshness {
  wbOrders: string | null
  wbSales: string | null
  ozonOrders: string | null
  funnel: string | null
}

export interface SalesSummary {
  current: SalesMetrics
  previous: SalesMetrics | null
  freshness: SalesFreshness
  /** Доля загруженных дней периода сравнения, 0..1 (Б2); нет поля — сравнение полное. */
  previousCoverage?: number | null
  /** Особенности определения метрик (тот же текст, что в каталоге метрик): `buyoutPercent` — разные даты WB. */
  notes?: Partial<Record<SalesMetricKey, string>>
}

export type SeriesGranularity = 'day' | 'week'
/** Метрика графика: сервер строит ряд по любой метрике `SalesMetrics` (параметр `metric`). */
export type SeriesMetric = SalesMetricKey

export interface SalesSeriesPoint {
  date: string
  value: number | null
  previousValue: number | null
}

export interface SalesSeriesEvent {
  date: string
  type: string
  label: string
}

export interface SalesSeries {
  points: SalesSeriesPoint[]
  /** Только при запросе нескольких метрик (`metrics=a,b,c`): ряд каждой метрики. */
  seriesByMetric?: Partial<Record<SalesMetricKey, SalesSeriesPoint[]>>
  events: SalesSeriesEvent[]
  previousCoverage?: number | null
}

/** Разрез разбивки — те же разрезы, что у общего провала вниз. */
export type BreakdownGroup = DrillDimension

export interface BreakdownItem {
  /** Ключ объекта (код площадки, id кабинета, № темы, код макета, артикул, ...). Пустой/`null` — «Не сопоставлено». */
  key: string | null
  label: string
  sublabel: string | null
  imageUrl: string | null
  /** Код макета (для разрезов `article` и `design`) — ссылка «Макет» строится по нему, а не по подписи. */
  designCode: string | null
  metrics: SalesMetrics
  previous: SalesMetrics | null
  /** Доля объекта в итоге, 0..1. */
  share: number | null
}

export interface SalesBreakdown {
  items: BreakdownItem[]
  total: number
  totals: SalesMetrics
  /** Итоги периода сравнения; `null` — режим «без сравнения» или сервер их не отдал. */
  previousTotals: SalesMetrics | null
  previousCoverage?: number | null
}

/** Ячейка «день недели × час» (часы — по Москве). */
export interface HourlyCell {
  /** 1 — понедельник … 7 — воскресенье. */
  weekday: number
  /** 0..23. */
  hour: number
  ordersQty: number | null
  ordersRub: number | null
}

export interface SalesHourly {
  cells: HourlyCell[]
  totalQty: number | null
}

export type HourlyMetric = 'ordersQty' | 'ordersRub'

export type MatrixMetric = 'ordersQty' | 'ordersRub' | 'buyoutsRub'

export interface MatrixRow {
  key: string | null
  label: string
  values: Array<number | null>
  total: number | null
}

export interface SalesMatrix {
  /** Недели вида `2026-W30`, от старой к новой. */
  weeks: string[]
  rows: MatrixRow[]
  /** Итог по неделям (если сервер отдал массив) — для строки «Всего». */
  weekTotals: Array<number | null> | null
  /** Общий итог периода (если сервер отдал число). */
  grandTotal: number | null
}
